/**
 * The handwriting answer pad (GAME_DESIGN.md § 6, docs/HANDWRITING.md).
 *
 * Write on the canvas → ~150 ms after each stroke the ink is recognised and
 * shown as chips (tap a chip to cycle its candidates) → ✓ / Enter submits.
 * Typing digits, "-", "." or "/" builds the answer too (sent as `kb`).
 * Unreadable or low-confidence answers fall back to multiple choice.
 *
 * All rules live in padModel.ts (pure, unit-tested); this component only
 * wires them to the canvas, timers and the keyboard. State is mirrored in a
 * ref so timers and key handlers always act on the latest version.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { HandwritingRecognizer, HwResult } from '../contracts.ts';
import { getLocale, useT } from '../i18n/index.ts';
import { displayChar, displayText, spokenCharKey } from './charDisplay.ts';
import { HwIcon } from './icons.tsx';
import { InkCanvas } from './InkCanvas.tsx';
import {
  type ChipView,
  type PadMessage,
  type PadState,
  PAD,
  decideSubmit,
  freshen,
  initialPadState,
  keyToAnswerChar,
  padReducer,
  readingOf,
} from './padModel.ts';
import './handwriting.css';

export interface HandwritingPadProps {
  recognizer: HandwritingRecognizer;
  /** Changes per problem; the pad clears itself when it changes. */
  resetKey: string;
  disabled: boolean;
  /** Watch layout: smaller pad, one character at a time, auto-append after a short pause. */
  compact?: boolean;
  onSubmit(text: string, via: 'hw' | 'kb'): void;
  /** User tapped "Choices", or recognition failed twice / stayed low-confidence. */
  onFallbackToChoices(reason: 'user' | 'recognition-failed'): void;
  onRecognized?(result: HwResult): void;
}

/** Key events aimed at a text field or a modified shortcut are not answer typing. */
function isForeignKeyEvent(e: KeyboardEvent): boolean {
  if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return true;
  const el = e.target instanceof HTMLElement ? e.target : null;
  return !!el && (el.isContentEditable || el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT');
}

function messageText(m: PadMessage, t: ReturnType<typeof useT>): string {
  switch (m.kind) {
    case 'empty':
      return t('hw.msgEmpty');
    case 'notNumber':
      return t('hw.msgNotNumber');
    case 'unreadable':
      return t('hw.msgUnreadable');
    case 'suggest':
      return t('hw.msgSuggest', { answer: displayText(m.text, getLocale()) });
    case 'fallback':
      return t('hw.msgFallback');
  }
}

const isUnsure = (c: ChipView) => !c.confirmed && c.confidence < PAD.UNSURE_CONFIDENCE;

export function HandwritingPad(props: HandwritingPadProps) {
  const { resetKey, disabled, compact = false } = props;
  const t = useT();
  const locale = getLocale();

  const [state, setState] = useState<PadState>(initialPadState);
  const stateRef = useRef(state);
  const propsRef = useRef(props);
  const recognizeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const commitTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /** An answer was sent for this problem: ignore further submits (double tap, fast Enter) until resetKey changes. */
  const answered = useRef(false);

  useLayoutEffect(() => {
    propsRef.current = props;
  });

  /** Apply a state transition synchronously (so the next event sees it) and re-render. */
  const update = useCallback((f: (s: PadState) => PadState): PadState => {
    const next = f(stateRef.current);
    if (next !== stateRef.current) {
      stateRef.current = next;
      setState(next);
    }
    return next;
  }, []);

  const clearTimers = useCallback(() => {
    clearTimeout(recognizeTimer.current);
    clearTimeout(commitTimer.current);
  }, []);

  const recognize = useCallback((strokes: PadState['strokes']) => propsRef.current.recognizer.recognize(strokes), []);

  /** Live recognition of the current ink (no-op when it is already up to date). */
  const recognizeNow = useCallback(() => {
    const s = stateRef.current;
    if (!s.strokes.length || (s.result && s.resultVersion === s.inkVersion)) return;
    const result = recognize(s.strokes);
    update((cur) => padReducer(cur, { type: 'recognized', version: s.inkVersion, result }));
    propsRef.current.onRecognized?.(result);
  }, [recognize, update]);

  /** Watch layout: move the character on the canvas to the answer line. */
  const commitNow = useCallback(() => {
    recognizeNow();
    update((s) => (s.result && s.resultVersion === s.inkVersion ? padReducer(s, { type: 'commit', version: s.inkVersion, result: s.result }) : s));
  }, [recognizeNow, update]);

  const scheduleCommit = useCallback(() => {
    if (propsRef.current.compact) commitTimer.current = setTimeout(commitNow, PAD.COMPACT_COMMIT_MS);
  }, [commitNow]);

  /** A stroke was added: read it after a short pause (a new stroke postpones this). */
  const scheduleRecognition = useCallback(() => {
    clearTimers();
    recognizeTimer.current = setTimeout(recognizeNow, PAD.RECOGNIZE_DELAY_MS);
    scheduleCommit();
  }, [clearTimers, recognizeNow, scheduleCommit]);

  /** Ink was removed: re-read what is left straight away, so no chip shows a deleted stroke. */
  const rereadAfterRemoval = useCallback(
    (next: PadState) => {
      if (!next.strokes.length) return;
      recognizeNow();
      scheduleCommit();
    },
    [recognizeNow, scheduleCommit],
  );

  // New problem: forget everything (before paint, so the old answer never flashes up).
  useLayoutEffect(() => {
    clearTimers();
    answered.current = false;
    update((s) => padReducer(s, { type: 'reset' }));
  }, [resetKey, clearTimers, update]);

  useEffect(() => clearTimers, [clearTimers]);

  const submit = useCallback(() => {
    if (propsRef.current.disabled || answered.current) return;
    clearTimers();
    const isCompact = !!propsRef.current.compact;
    const { state: next, effect } = decideSubmit(freshen(stateRef.current, isCompact, recognize), isCompact);
    update(() => next);
    if (effect?.kind === 'submit') {
      answered.current = true;
      propsRef.current.onSubmit(effect.text, effect.via);
    } else if (effect?.kind === 'fallback') {
      propsRef.current.onFallbackToChoices('recognition-failed');
    }
  }, [clearTimers, recognize, update]);

  const undo = () => {
    clearTimers();
    rereadAfterRemoval(update((s) => padReducer(s, { type: 'undo' })));
  };

  const clear = () => {
    clearTimers();
    update((s) => padReducer(s, { type: 'clear' }));
  };

  // Keyboard: digits and signs build the answer, Backspace edits, Enter submits.
  useEffect(() => {
    if (disabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (isForeignKeyEvent(e)) return;
      if (e.key === 'Enter') {
        // A focused button handles Enter itself (e.g. cycling a chip).
        if (e.repeat || (e.target instanceof HTMLElement && e.target.closest('button'))) return;
        e.preventDefault();
        submit();
        return;
      }
      if (e.key === 'Backspace') {
        e.preventDefault();
        clearTimers();
        rereadAfterRemoval(update((s) => padReducer(s, { type: 'backspace' })));
        return;
      }
      const char = keyToAnswerChar(e.key);
      if (!char) return;
      e.preventDefault();
      clearTimers();
      update((s) => padReducer(s, { type: 'typed', char }));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [disabled, submit, clearTimers, update, rereadAfterRemoval]);

  const reading = readingOf(state, compact);
  const hasInput = reading.source !== 'none' || state.strokes.length > 0;
  const unsure = reading.chips.some(isUnsure);
  const message = state.message ? messageText(state.message, t) : unsure ? t('hw.hintUnsure') : '';

  const chipLabel = (c: ChipView, i: number) => {
    const spoken = spokenCharKey(c.char);
    const params = { n: i + 1, count: reading.chips.length, char: spoken ? t(spoken) : c.char };
    return isUnsure(c) ? t('hw.chipLabelUnsure', params) : t('hw.chipLabel', params);
  };

  const classes = ['hw-pad', compact && 'hw-pad--compact', disabled && 'hw-pad--disabled'].filter(Boolean).join(' ');

  return (
    <div className={classes} role="group" aria-label={t('hw.padLabel')} aria-disabled={disabled || undefined}>
      <div className="hw-answer" role="group" aria-label={t('hw.answerLine')}>
        {reading.source === 'keyboard' ? (
          <span className="hw-answer__typed" role="status" aria-label={t('hw.typedLabel', { text: reading.text })}>
            {displayText(reading.text, locale)}
          </span>
        ) : (
          reading.chips.map((c, i) => (
            <button
              key={c.key}
              type="button"
              className={[
                'hw-chip',
                isUnsure(c) && 'hw-chip--unsure',
                c.confirmed && 'hw-chip--confirmed',
                c.pending && 'hw-chip--pending',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-label={chipLabel(c, i)}
              disabled={disabled}
              onClick={() => update((s) => padReducer(s, { type: 'cycle', key: c.key }))}
            >
              {displayChar(c.char, locale)}
            </button>
          ))
        )}
        {reading.source === 'none' && <span className="hw-answer__empty" aria-hidden="true">?</span>}
      </div>

      <InkCanvas
        className="hw-pad__ink"
        strokes={state.strokes}
        disabled={disabled}
        label={t('hw.padLabel')}
        guide
        placeholder={t(compact ? 'hw.placeholderCompact' : 'hw.placeholder')}
        onStrokeStart={clearTimers}
        onStrokeEnd={(stroke) => {
          update((s) => padReducer(s, { type: 'strokeAdded', stroke }));
          scheduleRecognition();
        }}
      />

      <p className="hw-pad__msg" aria-live="polite">
        {message}
      </p>

      <div className="hw-pad__buttons">
        <button
          type="button"
          className="hw-btn hw-btn--quiet hw-btn--choices"
          aria-label={t('hw.choicesLabel')}
          disabled={disabled}
          onClick={() => props.onFallbackToChoices('user')}
        >
          <HwIcon name="choices" />
          {!compact && <span className="hw-btn__text">{t('hw.choices')}</span>}
        </button>
        {!compact && (
          <button type="button" className="hw-btn" aria-label={t('hw.clearLabel')} disabled={disabled || !hasInput} onClick={clear}>
            <HwIcon name="clear" />
            <span className="hw-btn__text">{t('hw.clear')}</span>
          </button>
        )}
        <button type="button" className="hw-btn" aria-label={t('hw.undoLabel')} disabled={disabled || !hasInput} onClick={undo}>
          <HwIcon name="undo" />
          {!compact && <span className="hw-btn__text">{t('hw.undo')}</span>}
        </button>
        <button type="button" className="hw-btn hw-btn--go" aria-label={t('hw.submitLabel')} disabled={disabled} onClick={submit}>
          <HwIcon name="check" />
          {!compact && <span className="hw-btn__text">{t('hw.submit')}</span>}
        </button>
      </div>
    </div>
  );
}
